import React, {useEffect, useState} from 'react';

import {skipToken} from '@reduxjs/toolkit/query';
import {browserHistory, withRouter, WithRouterProps} from 'react-router';

import {convertLocationParamsFoldersDocumentsToFetchParams} from 'app/documents/api';
import {useFolderByHashid, useFolders} from 'app/folders/api';
import {EditFolder} from 'app/folders/components/dashboards/EditFolder';
import {FolderItem} from 'app/folders/components/FolderItem';
import {getFilterLink} from 'app/general/dashboards/DashboardFilterbar';
import {KBTreeDeleteItemContextProvider} from 'app/kbs/components/KBEditPage/KBTree/KBTreeDeleteItemModal';
import {KBTreeEditItemContextProvider} from 'app/kbs/components/KBEditPage/KBTree/KBTreeEditItemModal';
import {gt} from 'app/utils/i18n';

import {DashboardSidebarCollapsableList} from './DashboardSidebarCollapsableList';

import type {ProceduresDashboardLocationParamsType} from 'app/procedures/types';
import type {
  FolderHashidSerializedType,
  FolderSerializedType,
} from 'sweet-types';

type OwnProps = {
  item: FolderSerializedType;
};
type RouterProps = WithRouterProps<ProceduresDashboardLocationParamsType>;
type Props = OwnProps & RouterProps;

const FolderItemWrap = withRouter<Props>(({item, location}: Props) => {
  const hashid = location.query.folder;
  const [selected, setSelected] = useState<
    FolderHashidSerializedType | undefined
  >();
  const {data: folder, isFetching} = useFolderByHashid(
    hashid != null ? hashid : skipToken
  );

  useEffect(() => {
    setSelected(hashid != null ? folder : undefined);
  }, [folder, hashid, isFetching]);

  const onToggle = (expanded: boolean, folder: FolderSerializedType) => {
    const link = getFilterLink(
      browserHistory,
      browserHistory.getCurrentLocation(),
      {
        folder: folder.hashid === selected?.hashid ? undefined : folder.hashid,
      }
    );
    browserHistory.push(link);
  };

  return (
    <FolderItem
      folder={item}
      selectedHashid={location.query.folder}
      selectedFolder={selected}
      tooltipText={gt('Only procedures in this folder will be displayed')}
      isParentDirty={false}
      isHidingDocuments={true}
      isHidingCreateDocument={true}
      isHidingCreateFolder={false}
      isCreateFolderPopup={true}
      isHidingTeams={true}
      afterAction={() => Promise.resolve(true)}
      onToggle={onToggle}
    />
  );
});

export const DashboardSidebarFoldersList = () => {
  return (
    <KBTreeEditItemContextProvider FormComponent={EditFolder}>
      <KBTreeDeleteItemContextProvider>
        <DashboardSidebarCollapsableList
          icon="fa-folder"
          title={gt('Folders')}
          reduxKey="folders"
          watchModelChange="folder"
          showSearch={false}
          noResultsMessage={gt('No folders.')}
          canCreate={false}
          createLink={''}
          searchPlaceholder={gt('Search folders...')}
          useDataHook={useFolders}
          paramsConverter={convertLocationParamsFoldersDocumentsToFetchParams}
          pageParam={'folders_page'}
          ItemComponent={FolderItemWrap}
          itemNameProcessor={(item) => item.name}
        />
      </KBTreeDeleteItemContextProvider>
    </KBTreeEditItemContextProvider>
  );
};
